import { useQuery } from "@tanstack/react-query";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { ApiKeyTable } from "components/api-keys/api-keys-table";
import { PageTitle } from "components/page-title";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "components/ui/alert";
import { Button } from "components/ui/button";
import { Spinner } from "components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchApiKeys } from "data/queries-common";
import { MyAppsSubscriptionRequired } from "pages/developer/apps/subscription-required";
import { FirstAppEmptyState } from "pages/developer/apps/first-app-empty-state";
import { InfoIcon, Layers } from "lucide-react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

// Matched against ApiKey.products[].productName, so these must be the Apigee product names.
export const premiumProducts = [
  "Buildings API",
  "Land Parcels API",
  "Geoscape Verify",
];

export const ApiKeysPage = () => {
  const history = useHistory();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const apiKeysQuery = useQuery({
    queryKey: [QueryKeys.apiKeys, identityId],
    queryFn: () => fetchApiKeys(),
  });
  const hasNoApps = apiKeysQuery.data?.length === 0;

  const keysPremiumData = apiKeysQuery.data?.some((key: any) =>
    // products are {productName, productDisplayName} objects, so map before comparing
    key.products
      .map((product: any) => product.productName)
      .some((productName: string) => premiumProducts.includes(productName)),
  );

  const premiumDataPermission = [
    PermissionsKeys.premiumDataCreate,
    PermissionsKeys.premiumDataDelete,
    PermissionsKeys.premiumDataRead,
    PermissionsKeys.premiumDataUpdate,
  ];

  return (
    <div className="space-y-6">
      {!hasNoApps && (
        <PermissionsWrapper
          keys={[PermissionsKeys.appsCreate]}
          messages={{
            [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
          }}
          fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
        >
          <PageTitle
            icon={Layers}
            title="My Apps"
            description="Create and manage your Geoscape integrations and apps."
            actions={
              <PermissionsWrapper
                keys={[PermissionsKeys.appsCreate]}
                messages={{
                  [PermissionsReasons.ROLE_DENY]: <></>,
                  [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
                }}
              >
                <Button
                  disabled={apiKeysQuery.isPending}
                  onClick={() => history.push(Routes.developerApiKeysCreate)}
                >
                  {apiKeysQuery.isPending && <Spinner />}
                  Create App
                </Button>
              </PermissionsWrapper>
            }
          />
        </PermissionsWrapper>
      )}

      {/* We got this double wrapper so we only show you the 'upgrade for premium data' CTA  */}
      {!!apiKeysQuery.data?.length && (
        <PermissionsWrapper
          keys={premiumDataPermission}
          messages={{
            [PermissionsReasons.ROLE_DENY]: <></>,
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <PermissionsWrapper
                keys={[PermissionsKeys.appsCreate]}
                messages={{
                  [PermissionsReasons.UPGRADE_REQUIRED]: <></>,
                }}
                fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
              >
                <Alert variant="info" className="">
                  <InfoIcon />
                  <AlertTitle>
                    {keysPremiumData
                      ? "Premium APIs unavailable"
                      : "Upgrade for premium"}
                  </AlertTitle>
                  <AlertDescription>
                    {keysPremiumData
                      ? "Looks like some of your apps use premium data. To enable those APIs and data or create new apps with access to premium data, please upgrade to a paid plan."
                      : "Want more from My Apps? Upgrade to a paid plan for larger credit balances and access to premium APIs and data such as Land Parcels API, Buildings API, Geoscape Verify and MailPoint."}
                  </AlertDescription>
                  <AlertAction>
                    <UpgradeSubscriptionButton />
                  </AlertAction>
                </Alert>
              </PermissionsWrapper>
            ),
          }}
        >
          <></>
        </PermissionsWrapper>
      )}

      <div>
        <PermissionsWrapper
          keys={[PermissionsKeys.appsCreate]}
          messages={{
            [PermissionsReasons.UPGRADE_REQUIRED]: (
              <MyAppsSubscriptionRequired />
            ),
          }}
          fallbackToChildren={[PermissionsReasons.ROLE_DENY]}
        >
          <ReactQueryErrorWrapper queries={[apiKeysQuery]} mutations={[]} />
          {hasNoApps ? (
            <FirstAppEmptyState />
          ) : (
            apiKeysQuery.data && (
              <ApiKeyTable
                data={apiKeysQuery.data}
                isLoading={apiKeysQuery.isPending}
                apiKeyDetailsRoute={Routes.developerApiKeysEditKey}
                handleClick={() => {}}
                identityId={identityId}
              />
            )
          )}
        </PermissionsWrapper>
      </div>
    </div>
  );
};
